import { useEffect, useState } from "react";
import { MskScore } from "../../models/interfaces/msk-score";
import { User } from "../../models/interfaces/user";
import moment from "moment";
import { MovementHealthResponse } from "../../models/interfaces/reports/movement-health-response";
import NonLinearLineChart from "../charts/non-linear-line-chart";
import { Utils } from "../../utilities/utils";
import { useTranslation } from "react-i18next";
import UserProfilePainAreasAndScales from "../pain-areas/user-profile-pain-areas-and-scales";

const COMPONENT_CLASS = "c-user-profile-assessments";

// -----------------------------------------------------------------------------------------
// #region Intefaces
// -----------------------------------------------------------------------------------------

interface UserProfileBalanceReachProps {
    mskScore?: MskScore;
    validMskScore: boolean;
    user: User;
    allMskScores: MskScore[];
    setAllMskScores: Function;
    movementResponses?: MovementHealthResponse;

}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const UserProfileBalanceReachV2: React.FC<UserProfileBalanceReachProps> = (props: UserProfileBalanceReachProps) => {
    const { validMskScore, mskScore, allMskScores, movementResponses } = props;
    const [balanceReachHistory, setBalanceReachHistory] = useState<{ x: string, y: number }[]>();
    const { t } = useTranslation();

    useEffect(() => {
        const getHistory = () => {
            if (allMskScores) {
                let tempBalanceReachHistory = []

                for (const mskScore of allMskScores) {
                    tempBalanceReachHistory.push({
                        x: moment(mskScore && mskScore.created?.seconds ? mskScore.created.seconds * 1000 : NaN).format("MMM DD, YY"),
                        y: mskScore.movementScore?.balanceScore?.percentage ?? 0
                    })
                }
                setBalanceReachHistory(tempBalanceReachHistory);
            }
        }
        getHistory();
    }, [allMskScores])

    return (
        <>
            <div className="flex gap-5">
                <div className={`${COMPONENT_CLASS}__score-section-card gap-4 items-center`}>
                    <div className="text-base font-normal">{t('movementFocus.balanceAndReach')}</div>

                    <div className={`${COMPONENT_CLASS}__movement-group-score-card ${Utils.getGreenOrRedColor(mskScore?.movementScore?.balanceScore?.percentage, 75)}`}>
                        <div className={`${COMPONENT_CLASS}__movement-group-score-card__main-text`}>{mskScore?.movementScore?.balanceScore?.percentage}</div>
                        <div className={`${COMPONENT_CLASS}__movement-group-score-card__sub-text`}>{t('userProfile.assessments.goal75')}</div>
                    </div>
                    {validMskScore ?
                        <>
                            <div className="w-full flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                <span className="text-10 font-bold">{t('reports.wellness.oneFoot')}</span>
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.right'), mskScore?.movementScore?.balanceScore?.oneFootRight)}
                                {Utils.getAssessmentBreakdownCell(t('reports.wellness.left'), mskScore?.movementScore?.balanceScore?.oneFootLeft)}
                            </div>
                            <div className="flex gap-4">
                                <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                    <span className="text-10 font-bold">{t('reports.wellness.twoFeet')}</span>
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.rightAttempts', { attempts: mskScore?.movementScore?.balanceScore?.twoFeetRight.attempts }), mskScore?.movementScore?.balanceScore?.twoFeetRight.success)}
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.leftAttempts', { attempts: mskScore?.movementScore?.balanceScore?.twoFeetLeft.attempts }), mskScore?.movementScore?.balanceScore?.twoFeetLeft.success)}
                                </div>
                                <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                    <span className="text-10 font-bold">{t('reports.wellness.twoHalfFeet')}</span>
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.rightAttempts', { attempts: mskScore?.movementScore?.balanceScore?.threeFeetRight.attempts }), mskScore?.movementScore?.balanceScore?.threeFeetRight.success)}
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.leftAttempts', { attempts: mskScore?.movementScore?.balanceScore?.threeFeetLeft.attempts }), mskScore?.movementScore?.balanceScore?.threeFeetLeft.success)}
                                </div>
                            </div>
                        </>
                        : movementResponses ?
                            <>
                                <div className="w-full flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                    <span className="text-10 font-bold">{t('reports.wellness.oneFoot')}</span>
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.right'), movementResponses.firstBalanceReach.itemOne)}
                                    {Utils.getAssessmentBreakdownCell(t('reports.wellness.left'), movementResponses.firstBalanceReach.itemTwo)}
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                        <span className="text-10 font-bold">{t('reports.wellness.twoFeet')}</span>
                                        {Utils.getAssessmentBreakdownCell(t('reports.wellness.rightAttempts', { attempts: movementResponses.secondBalanceReachRight.attempts }), movementResponses.secondBalanceReachRight.success)}
                                        {Utils.getAssessmentBreakdownCell(t('reports.wellness.leftAttempts', { attempts: movementResponses.secondBalanceReachLeft.attempts }), movementResponses.secondBalanceReachLeft.success)}
                                    </div>
                                    <div className="flex flex-col p-4 gap-1 bg-black-gray-5" style={{ borderRadius: "10px" }}>
                                        <span className="text-10 font-bold">{t('reports.wellness.twoHalfFeet')}</span>
                                        {Utils.getAssessmentBreakdownCell(t('reports.wellness.rightAttempts', { attempts: movementResponses.thirdBalanceReachRight.attempts }), movementResponses.thirdBalanceReachRight.success)}
                                        {Utils.getAssessmentBreakdownCell(t('reports.wellness.leftAttempts', { attempts: movementResponses.thirdBalanceReachLeft.attempts }), movementResponses.thirdBalanceReachLeft.success)}
                                    </div>
                                </div>
                                <div>
                                    {Utils.getPainBadge(movementResponses.balanceQPain)}
                                </div>
                            </>
                            :
                            <div></div>
                    }

                </div>

                {balanceReachHistory && (
                    <div className={`${COMPONENT_CLASS}__score-section-card chart-wrapper grow`}>
                        <NonLinearLineChart
                            dataPoints={balanceReachHistory.map(history => history.y)}
                            labels={balanceReachHistory.map(history => history.x)}
                            title={t('userProfile.assessments.balanceHistory')}
                            sectionDetails={{
                                sections: 2,
                                rangeOne: { start: 0, end: 74 },
                                rangeTwo: { start: 75, end: 100 },
                                backgroundColorOrder: { first: "#FFA998", second: "#8BE3CE" },
                                dataPointColorOrder: { first: "#E7514F", second: "#00DFBC" },
                                yAxisLabels: { first: t('userProfile.assessments.below'), second: t('userProfile.assessments.above') },
                                decimals: false
                            }}
                            toolTipLabel={true} />
                    </div>
                )}
            </div>
            {mskScore?.movementScore?.balanceScore?.painAreasAndScales && mskScore?.movementScore?.balanceScore?.painAreasAndScales.length > 0 && (
                <div
                    className={`
                ${COMPONENT_CLASS}__score-section-card 
                my-5
            `}
                >
                    <UserProfilePainAreasAndScales
                        painAreasAndScales={mskScore?.movementScore?.balanceScore?.painAreasAndScales}
                    />
                </div >
            )}
        </>
    );
}

export default UserProfileBalanceReachV2;
